import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BlogTenantStatus } from '@/store/slices/blog';
import { Toggle } from '../../../../components/Toggle';
import { FtuxTarget } from '../../../../features/ftux/FtuxTarget';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { Root, StatusText } from './BlogEnableToggle.styles';

/*
 * Where the FTUX's blog-on piece lights the switch with its state word, the switch marked inside it (inert outside an
 * FTUX step). The shared Toggle forwards no attribute, so a span round it carries its mark.
 */
const BLOG_TOGGLE_TARGET: FtuxTargetId = 'blog-toggle';

export type BlogEnableToggleProps = {
  /** The tenant's answer, or null while it has not answered yet. */
  status: BlogTenantStatus | null;
  /** Why the tenant could not be read, when it could not. */
  error: string | null;
  /** A switch write is in flight (the page's shared write lock). */
  busy: boolean;
  onChange: (enabled: boolean) => void;
};

/**
 * The blog's on/off switch, sitting beside the page title.
 *
 * It reflects the brand's tenant on the blog service, where three different
 * facts all read as "off" to an owner and only one of them is theirs to change:
 *
 * - no tenant at all (the blog has never been turned on): the switch creates it
 * - a tenant that exists and is disabled: the switch turns it back on
 * - a banned tenant: force-disabled by the platform, so the switch stays locked
 *   because every enable against it is refused
 *
 * It locks while the state is still being READ, so the owner is never invited
 * to act on a value that is only a placeholder. A read that FAILED is the
 * opposite case and stays operable: this switch is the only control in the
 * product that can create a tenant, so locking it on a failed read was what
 * turned a blog that would not load into a blog that could never be launched
 * (SB-239). Enabling is idempotent and create-or-enable, nothing here depends
 * on knowing the current state first, and a refusal comes back as a toast
 * carrying the service's own explanation.
 */
export const BlogEnableToggle = ({ status, error, busy, onChange }: BlogEnableToggleProps): React.ReactElement => {
  const { t } = useTranslation();

  const on = status?.enabled === true;
  const banned = status?.banned === true;
  // No answer and no failure: the read is still in flight, so there is nothing
  // to act on yet. A failure is a state of its own and does NOT lock (above).
  const checking = status === null && error === null;
  const locked = checking || banned || busy;

  // With no status yet, the state IS the failure when there is one; the service
  // copy is already a locale key, so it renders through the same node.
  const label = status === null ? (error ?? 'Checking') : banned ? 'Suspended' : on ? 'Live' : 'Off';

  return (
    <Root title={banned ? t('This blog was suspended by the platform.') : undefined} data-ftux={BLOG_TOGGLE_TARGET}>
      <StatusText text={label} tag="span" />
      <FtuxTarget id="blog-switch">
        <Toggle on={on} label="Turn the blog on or off" disabled={locked} onToggle={() => onChange(!on)} />
      </FtuxTarget>
    </Root>
  );
};

export default BlogEnableToggle;
